import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    CardContent,
    CardMedia,
    useMediaQuery,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Button,
    Snackbar,
    Alert
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CelebrationIcon from '@mui/icons-material/Celebration';
import MainLayout from '../../components/MainLayout';
import SwitchListingOverlay from '../../components/SwitchListingOverlay';
import { PrimaryButton, SecondaryButton, CustomCard, ScreeningSetup, TourAvailabilityOverlay, DeactivateListingOverlay } from '../../components';
import ListingOnboardingOverlay from '../../components/ListingOnboardingOverlay';
import CheckoutOverlay from "../../components/CheckoutOverlay";
import NotificationBanner from '../../components/NotificationBanner';
import axiosClient from '../../services/axiosClient';
import EastIcon from '@mui/icons-material/East';
import { useAuth } from '../../hooks/useAuth';
import { trackFeatureUsage } from '../../services/FeatureTrackingService';
import ListingInfoOverlay from '../../components/home/ListingInfoOverlay';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SupportIcon from '@mui/icons-material/Support';

interface HomeProps {
    user: {
        id: string;
        email: string;
        listing_credit: number;
    };
    current_listing: {
        id: string | null;
        photo_url: string | null;
        is_feature: boolean;
        is_active: boolean;
        address: string | null;
        ai_email: string | null;
        description: string | null;
        screening_link: string | null;
    };
    listings: any[];
    upcoming_tours_count: number;
    tasks: any[];
}


const Home = (props: HomeProps) => {
    const { user } = useAuth();
    const isDesktop = useMediaQuery('(min-width:960px)');
    const listingId = props.current_listing.id;

    // Save listing ID to sessionStorage when it changes
    useEffect(() => {
        if (listingId) {
            sessionStorage.setItem('currentListingId', listingId);
        }
    }, [listingId]);

    // State for listing info overlay
    const [listingInfoOpen, setListingInfoOpen] = useState(false);
    // State for screening overlay
    const [screeningOpen, setScreeningOpen] = useState(false);
    // State for tour availability overlay
    const [availabilityOpen, setAvailabilityOpen] = useState(false);
    // State for tour availability
    const [hasTourAvailability, setHasTourAvailability] = useState(false);

    // State for deactivate overlay
    const [deactivateOpen, setDeactivateOpen] = useState(false);
    const [switchListingOpen, setSwitchListingOpen] = useState(false);
    const [checkoutOpen, setCheckoutOpen] = useState(false);
    const [imageFit, setImageFit] = useState<'cover' | 'contain'>('cover');
    const [onboardingOpen, setOnboardingOpen] = useState(false);
    const [notification, setNotification] = useState<{
        open: boolean;
        type: 'success' | 'error';
        message: string;
    }>({
        open: false,
        type: 'success',
        message: '',
    });
    const [screeningCriteria, setScreeningCriteria] = useState<any>(null);
    const [screeningLoading, setScreeningLoading] = useState(false);

    // Contact Support dialog/snackbar state
    const [supportOpen, setSupportOpen] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const [showScreeningCard, setShowScreeningCard] = useState(false);
    useEffect(() => {
        const hasSubmittedCriteria = screeningCriteria && Object.values(screeningCriteria).some(val => val !== null);
        const dismissed = localStorage.getItem('screening_card_dismissed') === 'true';
        setShowScreeningCard(!hasSubmittedCriteria && !dismissed);
        }, [screeningCriteria]);

    // Refresh screening criteria
    const refreshScreeningCriteria = () => {
        if (!listingId) return;
        setScreeningLoading(true);
        axiosClient.get(`/listings/${listingId}/get_screening_criteria`)
            .then(response => {
                setScreeningCriteria(response.data);
            })
            .catch(() => {
                setScreeningCriteria(null);
            })
            .finally(() => {
                setScreeningLoading(false);
            });
    };

    // Refresh tour availability
    const refreshTourAvailability = async () => {
        try {
          const response = await axiosClient.get('/tour_availabilities');
          const schedule = response.data.schedule || {};
          const hasAvailable = Object.values(schedule).some((day: any) => day.enabled && day.timeRanges.length > 0);
          setHasTourAvailability(hasAvailable);
        } catch (error) {
          console.error("Error fetching tour availability:", error);
          setHasTourAvailability(false); // fallback
        }
      };


    // Refresh screening criteria
    useEffect(() => {
        if (!listingId) return;
        refreshScreeningCriteria();
    }, [listingId]);

    // Refresh tour availability and auto-open overlay if not set
    useEffect(() => {
        const checkAndOpenAvailability = async () => {
            try {
                const response = await axiosClient.get('/tour_availabilities');
                const schedule = response.data.schedule || {};
                const hasAvailable = Object.values(schedule).some((day: any) => day.enabled && day.timeRanges.length > 0);
                setHasTourAvailability(hasAvailable);
                
                // Auto-open availability overlay if landlord doesn't have availability set
                if (!hasAvailable) {
                    setAvailabilityOpen(true);
                }
            } catch (error) {
                console.error("Error fetching tour availability:", error);
                setHasTourAvailability(false);
                // Also open the overlay on error to ensure landlord sets availability
                setAvailabilityOpen(true);
            }
        };
        
        checkAndOpenAvailability();
    }, []);


    // Handle query parameters for payment status
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const sessionId = urlParams.get('session_id');
        const failed = urlParams.get('failed');

        if (failed === 'true') {
            setNotification({
                open: true,
                type: 'error',
                message: 'An error processing payment. Please try again.',
            });
        }

        // Payment session Id
        if (sessionId) {
            // Check if sessionId exists in localStorage, if not add it
            let newPurchase = false
            const processedSessions = JSON.parse(localStorage.getItem('processedPaymentSessions') || '[]');
            if (!processedSessions.includes(sessionId)) {
                processedSessions.push(sessionId);
                localStorage.setItem('processedPaymentSessions', JSON.stringify(processedSessions));
                newPurchase = true;
            }

            if (!newPurchase) {
                console.log("Session ID already processed, not notifying user: ", sessionId);
                return;
            }
            const pollInterval = setInterval(async () => {
                try {
                    const creditResponse = await axiosClient.get('/listings/get_listing_credit');
                    const newCredit = creditResponse.data.listing_credit;
                    console.log("New credit: ", newCredit);
                    console.log("Current credit: ", props.user.listing_credit);

                    if (newCredit > 0) {
                        setNotification({
                            open: true,
                            type: 'success',
                            message: 'Payment successful! You can now create your listing.',
                        });
                        setOnboardingOpen(true);
                        clearInterval(pollInterval); // Stop polling once we see the credit increase
                    }
                } catch (error) {
                    console.error('Error polling for listing credit:', error);
                }
            }, 1000); // Poll every second

            // Stop polling after 10 seconds (webhook should have processed by then)
            setTimeout(() => clearInterval(pollInterval), 10000);

            return () => clearInterval(pollInterval);
        }
    }, []);

    const handleOpenScreening = () => {
        setScreeningOpen(true);
    };

    const handleCloseScreening = () => {
        setScreeningOpen(false);
    };

    const handleOpenAvailability = () => {
        setAvailabilityOpen(true);
    };

    const handleCloseAvailability = () => {
        setAvailabilityOpen(false);
    };

    const handleOpenDeactivate = () => {
        setDeactivateOpen(true);
    };

    const handleCloseDeactivate = () => {
        setDeactivateOpen(false);
    };

    const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
        const img = event.target as HTMLImageElement;
        const aspectRatio = img.naturalWidth / img.naturalHeight;
        setImageFit(aspectRatio > 1 ? 'cover' : 'contain');
    };

    const handleOpenSupport = () => {
        setSupportOpen(true);
    };

    const handleCloseSupport = () => {
        setSupportOpen(false);
    };

    const handleCopyToClipboard = async (text: string, label: string) => {
        try {
            await navigator.clipboard.writeText(text);
            setSnackbarMessage(`${label} copied to clipboard!`);
            setSnackbarOpen(true);
        } catch (err) {
            setSnackbarMessage('Failed to copy to clipboard');
            setSnackbarOpen(true);
        }
    };

    const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
    };

    // Common card styles for consistency
    return (
        <MainLayout
            headerButton={
                <SecondaryButton onClick={() => {
                if (user?.id) {
                    trackFeatureUsage(user.id, 'Switch Listing');
                }

                setSwitchListingOpen(true)}}>
                    Switch Listing
                </SecondaryButton>
            }
            currentListingId={listingId}
        >
            <NotificationBanner
                open={notification.open}
                type={notification.type}
                message={notification.message}
                onClose={() => {
                    setNotification({
                        type: notification.type,
                        message: notification.message,
                        open: false
                    });
                }}
            />
            <CustomCard>
                <Box sx={{ position: 'relative', width: '100%', height: '300px', overflow: 'hidden', backgroundColor: '#f5f5f5' }}>
                    <CardMedia
                        component="img"
                        image={props.current_listing.photo_url || "https://static.vecteezy.com/system/resources/previews/054/467/502/non_2x/abstract-house-illustration-yellow-red-and-blue-design-free-vector.jpg"}
                        alt="Property Image"
                        onLoad={handleImageLoad}
                        sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            minWidth: '100%',
                            minHeight: '100%',
                            objectFit: imageFit
                        }}
                    />
                </Box>
                <CardContent sx={{ p: 3 }}> {/* Consistent padding */}
                    <Typography variant="body1" fontWeight={500} gutterBottom>
                        {props.current_listing.address?.replace(", USA", "")}
                    </Typography>
                    <Box mt={2} display="flex" gap={1}>
                        {props.current_listing.is_active ? (
                                <Box sx={{ width: '100%' }}>
                                    <Box display="flex" gap={2} mb={1}>
                                        {/* TODO-R Not implemented yet but goes here*
                                        <SecondaryButton sx={{ flex: 1 }}>
                                            Edit listing
                                        </SecondaryButton>
                                        */}

                                    </Box>

                                    <Box display="flex" gap={1} mb={1} mt={2}>
                                        <SecondaryButton sx={{ flex: 1 }} onClick={() => {
                                            if (user?.id) {
                                                trackFeatureUsage(user.id, 'Listing Info');
                                            }
                                            setListingInfoOpen(true)}}
                                        >
                                            Listing Info
                                        </SecondaryButton>

                                        <SecondaryButton sx={{ flex: 1 }} onClick={handleOpenDeactivate}>
                                            Unit is filled!
                                        </SecondaryButton>
                                    </Box>
                                </Box>
                        ) : (
                            <Chip
                                icon={<CelebrationIcon />}
                                label="Listing Closed. Tenants found"
                                variant="outlined"
                                sx={{
                                    width: '100%',
                                    fontWeight: 600,
                                    border: 'none',
                                    fontFamily: "Inter",
                                    color: 'black',
                                    '& .MuiChip-label': {
                                        color: 'black'
                                    }
                                }}
                            />
                        )}
                    </Box>
                </CardContent>
            </CustomCard>

            {props.upcoming_tours_count > 0 && (
                <CustomCard>
                    <CardContent sx={{ p: 2}}> {/* Consistent padding */}
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Box display="flex" alignItems="baseline" gap={1}>
                                    <Typography fontSize= '35px' fontWeight='bold'>
                                        {props.upcoming_tours_count}
                                    </Typography>
                                <Typography fontSize="17.5px">Upcoming Tours</Typography>
                            </Box>
                            <EastIcon
                                sx={{ cursor: 'pointer', color: 'black' }}
                                onClick={() => window.location.href = '/home/tours'}
                            />
                        </Box>
                    </CardContent>
                </CustomCard>
            )}
            {/* New Listing Card */}
            <CustomCard
            >
                <CardContent sx={{ p: 3 }}> {/* Consistent padding */}
                    <Box display="flex" alignItems="center" mb={1} gap={1}>
                        <span style={{ fontSize: 26, marginRight: 2 }}>🏠</span>
                        <Typography fontWeight="bold">Another unit?</Typography>
                    </Box>

                    <Typography variant="body2" mb={3}>
                         Let us bring a qualified tenants to your door.
                    </Typography>
                    <Box display="flex" gap={1}>
                        <PrimaryButton
                            fullWidth
                            sx={{
                                alignSelf: 'center',
                                animation: 'breathing 2.2s ease-in-out infinite alternate',
                                '@keyframes breathing': {
                                    from: { transform: 'scale(1)' },
                                    to: { transform: 'scale(1.04)' },
                                },
                            }}
                            onClick={() =>  {
                                if (user?.id) {
                                    trackFeatureUsage(user.id, 'Create New Listing');
                                }
                                window.open('/listing/create', '_blank', 'noopener,noreferrer');
                              }}                        >
                            Create New Listing
                        </PrimaryButton>
                    </Box>
                </CardContent>
            </CustomCard>
            {/* Get First Tour Card */}
            {(
                <CustomCard >
                    <CardContent sx={{ p: 3 }}> {/* Consistent padding */}
                        <Box display="flex" alignItems="center" mb={1} gap={1}>
                            <InfoOutlinedIcon sx={{ color: '#grey.400' }} />
                            <Typography fontWeight="bold">Update your availability.</Typography>
                        </Box>
                        <Typography variant="body2" mb={3}>
                            Prospects select times that work for your schedule.
                        </Typography>
                        <SecondaryButton
                            fullWidth
                            onClick={handleOpenAvailability}
                        >
                            Update availability
                        </SecondaryButton>
                    </CardContent>
                </CustomCard>
            )}
            {/* Screening Card */}
            {(
            <CustomCard
            >
                <CardContent sx={{ p: 3 }}> {/* Consistent padding */}
                    <Box display="flex" alignItems="center" mb={1} gap={1}>
                        <InfoOutlinedIcon sx={{ color: '#grey.400' }} />
                        <Typography fontWeight="bold">Update screening for this listing</Typography>
                    </Box>
                    <Typography variant="body2" mb={3}>
                        Only get qualifed prospects to your door.
                    </Typography>
                    <Box display="flex" gap={1}>
                        <SecondaryButton
                            fullWidth
                            onClick={handleOpenScreening}
                        >
                            Update screening
                        </SecondaryButton>
                    </Box>
                </CardContent>
            </CustomCard>
            )}

            {/* Contact Support Card */}
            <CustomCard>
                <CardContent sx={{ p: 3 }}>
                    <Box display="flex" alignItems="center" mb={1} gap={1}>
                        <SupportIcon sx={{ color: 'grey.400' }} />
                        <Typography fontWeight="bold">Need Help?</Typography>
                    </Box>
                    <Typography variant="body2" mb={3}>
                        Contact our support team for assistance.
                    </Typography>
                    <SecondaryButton
                        fullWidth
                        onClick={handleOpenSupport}
                    >
                        Contact Support
                    </SecondaryButton>
                </CardContent>
            </CustomCard>

            {/* Use the ScreeningSetup Overlay component */}
            <ScreeningSetup
                open={screeningOpen}
                onClose={handleCloseScreening}
                isDesktop={isDesktop}
                listingId={props.current_listing.id || ''}
                criteria={screeningCriteria}
                loading={screeningLoading}
                onSuccess={refreshScreeningCriteria}
            />

            {/* Tour Availability Overlay */}
            <TourAvailabilityOverlay
                open={availabilityOpen}
                onClose={handleCloseAvailability}
                onSuccess={refreshTourAvailability}
            />

            {/* Deactivate Listing Overlay */}
            <DeactivateListingOverlay
                open={deactivateOpen}
                onClose={handleCloseDeactivate}
                listingId={props.current_listing.id || ''}
                isDesktop={isDesktop}
                onSuccess={() => {
                    // Update the current listing's is_active status
                    props.current_listing.is_active = false;
                }}
            />
            <SwitchListingOverlay
                open={switchListingOpen}
                onClose={() => setSwitchListingOpen(false)}
                listings={props.listings}
                currentListingId={listingId || ''}
            />
            <CheckoutOverlay
                open={checkoutOpen}
                onClose={() => setCheckoutOpen(false)}
                onError={() => {
                    setNotification({
                        open: true,
                        type: 'error',
                        message: 'An error processing payment. Please try again.',
                    });
                }}
                source="home"
                listingId={props.current_listing.id || ''}
            />
            <ListingOnboardingOverlay
                open={onboardingOpen}
                onClose={() => {
                    setOnboardingOpen(false);
                }}
            />
            <ListingInfoOverlay
                open={listingInfoOpen}
                onClose={() => setListingInfoOpen(false)}
                tasks={props.tasks}
                currentListingId={props.current_listing.id || ''}
                description={props.current_listing.description}
                screeningLink={props.current_listing.screening_link}
            />

            {/* Support Dialog */}
            <Dialog
                open={supportOpen}
                onClose={handleCloseSupport}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: '16px' } }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Contact Support
                    </Typography>
                    <IconButton onClick={handleCloseSupport} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <Box sx={{ mb: 3 }}>
                        <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                            Text or email us for assistance:
                        </Typography>

                        {/* Support Email */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, border: '1px solid', borderColor: 'grey.300', borderRadius: '8px', mb: 2 }}>
                            <Box>
                                <Typography variant="body2" color="grey.600" sx={{ mb: 0.5 }}>
                                    Email
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    rich@heyyproperty.com
                                </Typography>
                            </Box>
                            <Button variant="outlined" size="small" startIcon={<ContentCopyIcon />} onClick={() => handleCopyToClipboard('rich@heyyproperty.com', 'Email')} sx={{ minWidth: 'auto', px: 2 }}>
                                Copy
                            </Button>
                        </Box>

                        {/* Support Phone */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, border: '1px solid', borderColor: 'grey.300', borderRadius: '8px', mb: 3 }}>
                            <Box>
                                <Typography variant="body2" color="grey.600" sx={{ mb: 0.5 }}>
                                    Phone
                                </Typography>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                    617-459-2265
                                </Typography>
                            </Box>
                            <Button variant="outlined" size="small" startIcon={<ContentCopyIcon />} onClick={() => handleCopyToClipboard('617-459-2265', 'Phone number')} sx={{ minWidth: 'auto', px: 2 }}>
                                Copy
                            </Button>
                        </Box>

                        {/* Customer ID Section */}
                        <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: '8px', border: '1px solid', borderColor: 'grey.200' }}>
                            <Typography variant="body2" color="grey.600" sx={{ mb: 1 }}>
                                Your Customer ID
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace', fontSize: '0.9rem' }}>
                                    {props.user.id}
                                </Typography>
                                <Button variant="outlined" size="small" startIcon={<ContentCopyIcon />} onClick={() => handleCopyToClipboard(props.user.id, 'Customer ID')} sx={{ minWidth: 'auto', px: 2 }}>
                                    Copy
                                </Button>
                            </Box>
                            <Typography variant="body2" color="grey.600" sx={{ mt: 1, fontSize: '0.8rem' }}>
                                When you contact support, give us your ID so we can find you in our system.
                            </Typography>
                        </Box>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Snackbar for copy feedback */}
            <Snackbar open={snackbarOpen} autoHideDuration={3000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </MainLayout>
    );
};

export default Home;